import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  IconButton,
  Paper,
  Select,
  MenuItem,
} from '@mui/material';
import './RequestedTagTable.css';
import InfoIcon from '@mui/icons-material/Info';
import { formatText, formatDate } from '../../../../../utils/textUtils';
import usePagination from '@mui/material/usePagination';

const RequestedTagTable = ({
  tagData,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const { items } = usePagination({
    count: Math.ceil((totalCount || 0) / pageSize),
    page: currentPage,
    onChange: (event, value) => onPageChange(value),
  });

  const handleRowsPerPageChange = event => {
    onPageSizeChange(event.target.value);
  };

  const getTeacherName = teacher => {
    if (!teacher) return '-';
    return `${teacher.first_name} ${teacher.last_name}`;
  };

  const getStatusColor = status => {
    if (!status) return { color: '#999', label: '-' };

    switch (status) {
      case 'approved':
        return { color: '#4CAF50', label: 'Approved' };
      case 'rejected':
        return { color: '#F44336', label: 'Rejected' };
      default:
        return { color: '#999', label: formatText(status) };
    }
  };

  return (
    <Box>
      <Box className="requested-tag-table-container">
        <TableContainer component={Paper} elevation={0}>
          <Table stickyHeader aria-label="requested tags table">
            <TableHead>
              <TableRow>
                <TableCell className="requested-tag-table-header">Tag Name</TableCell>
                <TableCell className="requested-tag-table-header">Instructor Name</TableCell>
                <TableCell className="requested-tag-table-header">Subject</TableCell>
                <TableCell align="center" className="requested-tag-table-header">
                  Status
                </TableCell>
                <TableCell className="requested-tag-table-header">Created At</TableCell>
                <TableCell className="requested-tag-table-header">Action Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tagData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    className="requested-tag-table-cell"
                    style={{ padding: '24px 16px' }}
                  >
                    No tag requests found
                  </TableCell>
                </TableRow>
              ) : (
                tagData.map(row => {
                  const statusInfo = getStatusColor(row.status);
                  const actionDate = row.status === 'approved' ? row.approved_at : row.rejected_at;
                  let statusClass = 'requested-tag-status-chip';
                  if (row.status === 'approved')
                    statusClass += ' requested-tag-status-chip-approved';
                  else if (row.status === 'rejected')
                    statusClass += ' requested-tag-status-chip-rejected';
                  else if (row.status === 'pending')
                    statusClass += ' requested-tag-status-chip-pending';
                  return (
                    <TableRow hover key={row.id} className="requested-tag-table-row">
                      <TableCell className="requested-tag-table-cell">
                        <Typography fontWeight={500}>{formatText(row.title)}</Typography>
                      </TableCell>
                      <TableCell className="requested-tag-table-cell">
                        {getTeacherName(row.teacher)}
                      </TableCell>
                      <TableCell className="requested-tag-table-cell">
                        {formatText(row.subject_title)}
                      </TableCell>
                      <TableCell align="center" className="requested-tag-table-cell">
                        <Box className={statusClass}>
                          <span
                            className="requested-tag-status-dot"
                            style={{ backgroundColor: statusInfo.color }}
                          ></span>
                          <Typography sx={{ fontWeight: 500 }}>{statusInfo.label}</Typography>
                          {row.reason && (
                            <Tooltip
                              title={
                                <Typography sx={{ p: 1 }}>
                                  {row.status === 'rejected' ? 'Rejection reason: ' : ''}
                                  {row.reason}
                                </Typography>
                              }
                            >
                              <IconButton size="small" className="requested-tag-info-icon">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="requested-tag-table-cell">
                        {formatDate(row.created_at, 'dd-MM-yyyy')}
                      </TableCell>
                      <TableCell className="requested-tag-table-cell">
                        {formatDate(actionDate, 'dd-MM-yyyy')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination controls */}
      <Box className="requested-tag-pagination-container">
        <Box className="requested-tag-pagination-box">
          <Typography className="requested-tag-pagination-text">Showing</Typography>
          <Select
            value={pageSize}
            onChange={handleRowsPerPageChange}
            className="requested-tag-select"
          >
            {[10, 15, 20, 30].map(num => (
              <MenuItem key={num} value={num}>
                {num}
              </MenuItem>
            ))}
          </Select>
          <Typography className="requested-tag-pagination-total-text">
            of {totalCount || 0} tags
          </Typography>
        </Box>

        <nav>
          <ul className="requested-tag-pagination-list">
            {items.map(({ page: itemPage, type, selected, ...item }, index) => {
              let children = null;

              if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                children = '…';
              } else if (type === 'page') {
                children = (
                  <button
                    type="button"
                    className={`requested-tag-pagination-btn${selected ? ' requested-tag-pagination-btn-selected' : ''}`}
                    {...item}
                  >
                    {itemPage}
                  </button>
                );
              } else {
                children = (
                  <button type="button" className="requested-tag-pagination-btn" {...item}>
                    {formatText(type)}
                  </button>
                );
              }

              return <li key={index}>{children}</li>;
            })}
          </ul>
        </nav>
      </Box>
    </Box>
  );
};

export default RequestedTagTable;
